import { useEffect, useState } from 'react';
import { GlowOrb } from './NavBar';
import { blogImages } from '../images';

export function TestimonialsSection() {
  const testimonials = [
    {
      quote: '从人工巡检到全自动，0xman 让我们的仓储运营进入了新维度。机器狗的厘米级定位和自主导航能力远超预期。',
      author: '李建国',
      role: '运营总监',
      company: '华航物流集团',
    },
    {
      quote: '风电场没人敢去的地方，机器狗替我们去了，而且看得更准。AR 眼镜的实时标注让现场工程师拥有了透视能力。',
      author: '王晓明',
      role: '安全主管',
      company: '远景能源科技',
    },
    {
      quote: '以前测一组数据要半天，现在机器狗跑一圈，三维点云报告就生成了。施工效率提升了至少 3 倍。',
      author: '张伟',
      role: '项目经理',
      company: '中铁十四局',
    },
  ];

  const [current, setCurrent] = useState(0);
  const [isAuto, setIsAuto] = useState(true);

  useEffect(() => {
    if (!isAuto) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAuto, testimonials.length]);

  const goTo = (index: number) => {
    setCurrent(index);
    setIsAuto(false);
    setTimeout(() => setIsAuto(true), 10000);
  };

  const prev = () => goTo((current - 1 + testimonials.length) % testimonials.length);
  const next = () => goTo((current + 1) % testimonials.length);

  return (
    <section className="reveal relative py-24 overflow-hidden">
      <GlowOrb className="top-0 right-1/4 w-[500px] h-[500px] bg-primary-500/5" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16 text-center">
          <p className="text-xs font-mono tracking-[0.2em] text-primary-400 uppercase mb-3">Testimonials</p>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-50 mb-4">客户的声音</h2>
        </div>

        <div className="relative max-w-3xl mx-auto">
          <div className="relative rounded-2xl border border-background-800/60 bg-background-900/40 p-8 md:p-10 overflow-hidden">
            <span className="absolute top-6 left-6 w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10">
              <i className="ri-double-quotes-l text-xl text-primary-400" />
            </span>

            <div className="relative z-10 pt-8 min-h-[200px]">
              {testimonials.map((t, i) => (
                <div
                  key={t.company}
                  className={`transition-opacity duration-700 ease-in-out ${
                    i === current ? 'opacity-100 relative' : 'opacity-0 absolute inset-0 pt-8 pointer-events-none'
                  }`}
                >
                  <p className="text-lg md:text-xl text-foreground-200 leading-relaxed mb-8 font-medium">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/20 border border-primary-500/20">
                      <span className="text-sm font-bold text-primary-300">{t.author[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-200">{t.author}</p>
                      <p className="text-xs text-foreground-500">{t.role} · {t.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <button onClick={prev} className="testimonial-arrow w-10 h-10 flex items-center justify-center rounded-full border border-background-700 text-foreground-400 hover:text-primary-300 cursor-pointer">
              <i className="ri-arrow-left-line" />
            </button>
            <div className="flex items-center gap-2">
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => goTo(i)} className={`testimonial-dot h-2 rounded-full transition-all cursor-pointer ${i === current ? 'w-6 bg-primary-400' : 'w-2 bg-background-700'}`} />
              ))}
            </div>
            <button onClick={next} className="testimonial-arrow w-10 h-10 flex items-center justify-center rounded-full border border-background-700 text-foreground-400 hover:text-primary-300 cursor-pointer">
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CustomerCasesSection() {
  const cases = [
    {
      company: '华航物流集团', industry: '智慧仓储', metric: '↑ 340%', metricLabel: '巡检效率提升',
      desc: '部署 6 台机器狗覆盖 12,000㎡ 智能仓，实现 24h 自主盘点与异常告警，人工巡检成本下降 67%。',
      tags: ['机器狗巡检', '库存盘点', '异常告警'],
      quote: '"从人工巡检到全自动，0xman 让我们的仓储运营进入了新维度。"',
      color: 'primary',
    },
    {
      company: '远景能源科技', industry: '新能源运维', metric: '↓ 82%', metricLabel: '安全事故率降低',
      desc: '风机叶片与变电站高危区域交由机器狗+AR 眼镜协同巡检，AI 实时识别设备热斑与裂纹隐患。',
      tags: ['风机巡检', '热斑识别', 'AR 协同'],
      quote: '"风电场没人敢去的地方，机器狗替我们去了，而且看得更准。"',
      color: 'accent',
    },
    {
      company: '中铁十四局', industry: '工程施工', metric: '< 2h', metricLabel: '单次隧道扫描耗时',
      desc: '隧道施工段三维点云实时建模，变形监测精度达毫米级，支撑盾构掘进动态纠偏决策。',
      tags: ['点云建模', '变形监测', '盾构纠偏'],
      quote: '"以前测一组数据要半天，现在机器狗跑一圈，报告就生成了。"',
      color: 'primary',
    },
    {
      company: '海康智安', industry: '安防巡逻', metric: '24/7', metricLabel: '全天候自主值守',
      desc: '园区周界机器狗+无人机空地一体巡逻，夜间入侵识别准确率 99.2%，联动安保响应时间 < 15s。',
      tags: ['周界巡逻', '入侵识别', '空地联动'],
      quote: '"夜间园区安全真正实现了无人化闭环，这是我们盼了很多年的方案。"',
      color: 'accent',
    },
  ];

  return (
    <section id="案例" className="reveal relative py-24 overflow-hidden">
      <GlowOrb className="top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary-500/5" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <p className="text-xs font-mono tracking-[0.2em] text-primary-400 uppercase mb-3">Customer Stories</p>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-50 mb-4">客户实证，数据说话</h2>
          <p className="text-base text-foreground-400 max-w-2xl">从仓储物流到能源运维，从工程施工到安防巡逻，0xman 已服务数十家行业头部客户。</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {cases.map((c) => (
            <div key={c.company} className="group p-6 rounded-2xl border border-background-800/60 bg-background-900/40 hover:border-primary-500/30 transition-all card-lift">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-800 border border-background-700">
                    <span className="text-sm font-bold text-foreground-300">{c.company.slice(0, 1)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-200">{c.company}</p>
                    <p className="text-[11px] text-foreground-500">{c.industry}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-xl font-bold font-heading ${c.color === 'accent' ? 'text-accent-400' : 'text-primary-400'}`}>{c.metric}</p>
                  <p className="text-[10px] text-foreground-500">{c.metricLabel}</p>
                </div>
              </div>
              <p className="text-sm text-foreground-400 leading-relaxed mb-4">{c.desc}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {c.tags.map((tag) => (
                  <span key={tag} className={`px-2.5 py-1 rounded-full text-[10px] border ${c.color === 'accent' ? 'bg-accent-500/10 text-accent-300 border-accent-500/10' : 'bg-primary-500/10 text-primary-300 border-primary-500/10'}`}>{tag}</span>
                ))}
              </div>
              <div className="pt-4 border-t border-background-800/50">
                <p className="text-xs text-foreground-500 italic leading-relaxed">{c.quote}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TechBlogSection() {
  const articles = [
    {
      image: blogImages[0], tag: '空间智能', date: '2026.07.18',
      title: 'Physical AI 时代：为什么空间坐标系是下一代 AI 的基础设施',
      desc: '从 LLM 到 Physical AI，智能体需要的不只是语言理解，而是对三维物理世界的精准定位与记忆。',
      readTime: '8 min',
    },
    {
      image: blogImages[1], tag: '具身智能', date: '2026.07.10',
      title: '机器狗自主导航的「最后一公里」：从 SLAM 到语义决策',
      desc: '纯几何导航已不够。我们如何让机器狗理解「绕过障碍」与「识别异常」之间的语义差距？',
      readTime: '6 min',
    },
    {
      image: blogImages[2], tag: '产品发布', date: '2026.06.28',
      title: '0xman HUB v2.4 发布：多终端协同调度与实时数字孪生',
      desc: '全新控制台支持异构终端混编任务、实时态势大屏与一键远程诊断，让 fleet 管理像指挥舰队一样直觉。',
      readTime: '5 min',
    },
  ];

  return (
    <section id="博客" className="reveal relative py-24 overflow-hidden">
      <GlowOrb className="bottom-0 right-1/4 w-[500px] h-[500px] bg-accent-500/5" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <p className="text-xs font-mono tracking-[0.2em] text-primary-400 uppercase mb-3">Insights & Research</p>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-50 mb-4">技术洞察与前沿思考</h2>
          <p className="text-base text-foreground-400 max-w-2xl">0xman 研究团队持续输出空间智能、Physical AI 与具身智能的深度技术文章。</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {articles.map((a) => (
            <div key={a.title} className="group rounded-2xl border border-background-800/60 bg-background-900/40 overflow-hidden hover:border-primary-500/30 transition-all cursor-pointer card-lift">
              <div className="h-44 overflow-hidden">
                <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-2 py-0.5 rounded bg-primary-500/10 text-[10px] text-primary-300 border border-primary-500/10">{a.tag}</span>
                  <span className="text-[10px] text-foreground-600">{a.date}</span>
                  <span className="text-[10px] text-foreground-600">· {a.readTime} 阅读</span>
                </div>
                <h3 className="text-sm font-semibold text-foreground-200 leading-snug mb-2 group-hover:text-primary-300 transition-colors">{a.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{a.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}